var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { PathFinder } from '@blocksuite/block-std';
import { assertExists } from '@blocksuite/global/utils';
import { property, state } from 'lit/decorators.js';
import { WithDisposable } from '../with-disposable.js';
import { ShadowlessElement } from './shadowless-element.js';
export class BlockElement extends WithDisposable(ShadowlessElement) {
    constructor() {
        super(...arguments);
        this.content = null;
        this.selected = null;
        this.handleEvent = (name, handler, options) => {
            assertExists(this.path, 'Cannot bind block level hotkey without path');
            const config = {
                flavour: options?.global
                    ? undefined
                    : options?.flavour
                        ? this.model.flavour
                        : undefined,
                path: options?.global || options?.flavour ? undefined : this.path,
            };
            this._disposables.add(this.host.event.add(name, handler, config));
        };
        this.renderModel = (model) => {
            return this.host.renderModel(model);
        };
        this.renderModelChildren = (model) => {
            return this.host.renderModelChildren(model);
        };
    }
    get parentPath() {
        return this.path.slice(0, -1);
    }
    get parentBlockElement() {
        const parentElement = this.parentElement;
        assertExists(parentElement);
        const nodeView = this.host.view.getNodeView(parentElement);
        assertExists(nodeView);
        return nodeView.view;
    }
    get childBlockElements() {
        const children = this.host.view.getChildren(this.path);
        return children
            .filter(child => child.type === 'block')
            .map(child => child.view);
    }
    get flavour() {
        return this.model.flavour;
    }
    get widgetElements() {
        return Object.keys(this.widgets).reduce((mapping, key) => {
            return {
                ...mapping,
                [key]: this.host.view.viewFromPath('widget', [...this.path, key]),
            };
        }, {});
    }
    get service() {
        return this.host.std.spec.getService(this.model.flavour);
    }
    get selection() {
        return this.host.selection;
    }
    get std() {
        return this.host.std;
    }
    bindHotKey(keymap, options) {
        assertExists(this.path, 'Cannot bind block level hotkey without path');
        const config = {
            flavour: options?.global
                ? undefined
                : options?.flavour
                    ? this.model.flavour
                    : undefined,
            path: options?.global || options?.flavour ? undefined : this.path,
        };
        this._disposables.add(this.host.event.bindHotkey(keymap, config));
    }
    async getUpdateComplete() {
        const result = await super.getUpdateComplete();
        await Promise.all(this.childBlockElements.map(el => el.updateComplete));
        return result;
    }
    connectedCallback() {
        super.connectedCallback();
        this.path = this.host.view.calculatePath(this);
        this._disposables.add(this.model.propsUpdated.on(() => {
            this.requestUpdate();
        }));
        this._disposables.add(this.model.childrenUpdated.on(() => {
            this.requestUpdate();
        }));
        this._disposables.add(this.host.selection.slots.changed.on(selections => {
            const selection = selections.find(selection => {
                return PathFinder.equals(selection.path, this.path);
            });
            if (!selection) {
                this.selected = null;
                return;
            }
            this.selected = selection;
        }));
    }
    disconnectedCallback() {
        super.disconnectedCallback();
    }
    render() {
        return null;
    }
}
__decorate([
    property({ attribute: false })
], BlockElement.prototype, "host", void 0);
__decorate([
    property({ attribute: false })
], BlockElement.prototype, "model", void 0);
__decorate([
    property({ attribute: false })
], BlockElement.prototype, "content", void 0);
__decorate([
    property({
        attribute: false,
        hasChanged(value, oldValue) {
            if (!value || !oldValue) {
                return value !== oldValue;
            }
            // Is empty object
            if (!Object.keys(value).length && !Object.keys(oldValue).length) {
                return false;
            }
            return value !== oldValue;
        },
    })
], BlockElement.prototype, "widgets", void 0);
__decorate([
    property({ attribute: false })
], BlockElement.prototype, "page", void 0);
__decorate([
    state({
        hasChanged(value, oldValue) {
            if (!value || !oldValue) {
                return value !== oldValue;
            }
            return !value?.equals(oldValue);
        },
    })
], BlockElement.prototype, "selected", void 0);
//# sourceMappingURL=block-element.js.map